import { inject, Injectable } from '@angular/core';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, shareReplay, switchMap, take } from 'rxjs/operators';
import { ElasticResult } from '../../core/db/elastic.models';
import { Finishing, PageOrientation, PageSize, PagesPerSheet, PageTurn, PaperWeight, PrintColor, PrintSettings, PrintSettingsConfig, Settings, SettingsService } from '../../core/settings';
import { Category } from '../category/category.model';
import { UserModel } from '../user/user.models';
import { ProductDbService } from './product-db.service';
import {
  BindingRingResult,
  DISCOUNT_AVAILABILITY,
  findBindingRing,
  getAdjustedPrice,
  getFilePrice,
  getFileSheets,
  getFinishing,
  getFinishingWeight,
  getPriceOption,
  getPrintingGroupSheetsWeight,
  getTotalSides,
  isAllProductSoldOut
} from './product.helpers';
import {
  _PrintFile,
  BindingCoverUsage,
  BindingRingUsage,
  CoverTypeConfig,
  ExtraFinishingOption,
  ExtraPrices,
  HardCoverPrice,
  PrintingGroup,
  PrintVariant,
  Product,
  ProductCustomizableElements,
  ProductPrint,
  ProductSnapshot,
  ProductType,
  ProductVariant,
  ProductWithVariants,
  RingColorPrice,
  WeightMatrix
} from './product.models';

/**
 * Central product domain service.
 * Orchestrates data access (via {@link ProductDbService}), the print pricing
 * engine, variant logic, and UI transformations.
 */
@Injectable({ providedIn: 'root' })
export class ProductService {

  private productDbService = inject(ProductDbService);
  private settingsService = inject(SettingsService);

  // ─── Print settings state ─────────────────────────────────────────────────

  private readonly _settingsChanged = new BehaviorSubject<Date>(new Date());
  readonly settingsChanged$ = this._settingsChanged.asObservable();

  printingGroupFiles: _PrintFile[] = [];

  // ─── Print engine state ────────────────────────────────────────────────────

  private isInit = new BehaviorSubject<boolean>(false);
  readonly isInit$ = this.isInit.asObservable();

  tablePrices: Array<PrintVariant> = [];
  weights: WeightMatrix = {};
  discounts = DISCOUNT_AVAILABILITY;

  private printProduct: ProductPrint;
  private bindingData: any;
  private blankSheetPercentageCalc = 100;
  private ringColorPrice: RingColorPrice;
  private hardCoverPrice: HardCoverPrice;
  private extraPrices: ExtraPrices;

  papers$: Observable<ProductWithVariants>;
  customizablePapers$: Observable<ProductWithVariants>;
  combinedPapers: any[];

  /** Live stream of the base print product (id='0') from DB. */
  readonly printProductDb$: Observable<ProductPrint> = this.productDbService.get$('0').pipe(shareReplay({ refCount: true, bufferSize: 1 })) as Observable<ProductPrint>;

  mustReduceTax = false;
  productOpened: { product: any; context: any; } | null = null;

  // ─── Single product ────────────────────────────────────────────────────────

  /**
   * Retrieves a single product by its Firestore document ID as a live stream.
   * @param id - The Firestore document ID of the product.
   * @returns An Observable that emits the {@link Product} and updates on changes.
   */
  getProduct$(id: string): Observable<Product> {
    return this.productDbService.get$(id) as Observable<Product>;
  }

  /**
   * Retrieves a single product by its Firestore document ID as a one-shot promise.
   * @param id - The Firestore document ID of the product.
   * @returns A Promise resolving to the {@link Product}.
   */
  getProduct(id: string): Promise<Product> {
    return this.productDbService.get(id) as Promise<Product>;
  }

  /**
   * Retrieves a single product by its URL slug as a live stream.
   * @param slug - The unique slug associated with the product.
   * @returns An Observable emitting the matching {@link Product}.
   */
  getProductBySlug$(slug: string): Observable<Product> {
    return this.productDbService.getOneWhere$({ where: { slug } }) as Observable<Product>;
  }

  /**
   * Retrieves a product by ID together with its sorted variants as a live stream.
   * @param id - The Firestore document ID of the product.
   * @returns An Observable emitting the {@link Product} with its `variants` array populated.
   */
  getProductWithVariants$(id: string): Observable<ProductWithVariants> {
    return this.getProduct$(id).pipe(
      mergeMap(product =>
        this.getSortedVariants$(product).pipe(
          map(variants => ({ ...product, variants } as ProductWithVariants)),
        ),
      ),
    );
  }

  /**
   * Retrieves a product by slug together with its sorted variants as a live stream.
   * @param slug - The unique slug associated with the product.
   * @returns An Observable emitting the {@link Product} with its `variants` array populated.
   */
  getProductBySlugWithVariants$(slug: string): Observable<ProductWithVariants> {
    return this.getProductBySlug$(slug).pipe(
      mergeMap(product =>
        this.getSortedVariants$(product).pipe(
          map(variants => ({ ...product, variants } as ProductWithVariants)),
        ),
      ),
    );
  }

  // ─── Product lists ─────────────────────────────────────────────────────────

  /**
   * Returns a live stream of all products belonging to a given vendor.
   * @param vendorId - The ID of the vendor.
   * @returns An Observable emitting an array of {@link Product} objects.
   */
  getProductsByVendor$(vendorId: string): Observable<Product[]> {
    return this.productDbService.getWhere$({ where: { 'vendor.id': vendorId }, limit: 100 }) as Observable<Product[]>; // TODO JUAN: en algún momento luego hay que hacer algo con este limit
  }

  // ─── Variants ──────────────────────────────────────────────────────────────

  /**
   * Returns a live stream of all variants for a given product (up to 1 000).
   * @param productId - The Firestore document ID of the parent product.
   * @returns An Observable emitting an array of {@link ProductVariant} objects.
   */
  getVariants$(productId: string): Observable<ProductVariant[]> {
    return this.productDbService.getWhere$<ProductVariant>(
      { limit: 1000 }, // TODO JUAN: en algún momento luego hay que hacer algo con este limit, paginar o usar allowGetAll
      { subPath: `${productId}/variants` },
    );
  }

  /**
   * Returns a live stream of variants sorted according to the order defined on the product.
   * @param product - The parent {@link Product} whose `variants` field defines the sort order.
   * @returns An Observable emitting the ordered array of {@link ProductVariant} objects.
   */
  getSortedVariants$(product: Product): Observable<ProductVariant[]> {
    return this.getVariants$(product.id).pipe(
      map(variants =>
        product.variantCount && product.variants
          ? (product.variants as any[]).map(variantId => variants.find(v => v.id === variantId))
          : [],
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  /**
   * Returns a live stream for a single variant of a product.
   * @param productId - The Firestore document ID of the parent product.
   * @param variantId - The Firestore document ID of the variant.
   * @returns An Observable emitting the {@link ProductVariant}.
   */
  getVariant$(productId: string, variantId: string): Observable<ProductVariant> {
    return this.productDbService.get$<ProductVariant>(variantId, { subPath: `${productId}/variants` });
  }

  // ─── Internal products ─────────────────────────────────────────────────────

  /**
   * Looks up an internal product by its `internalId` field and hydrates it with sorted variants.
   * @param internalId - The internal identifier string (e.g. `'papers'`).
   * @returns An Observable emitting the {@link Product} with its `variants` array populated.
   */
  getInternalProductWithVariants$(internalId: string): Observable<ProductWithVariants> {
    return (this.productDbService.getWhere$({ where: { internalId }, limit: 10 }) as Observable<Product[]>).pipe(
      mergeMap(products =>
        this.getSortedVariants$(products[0]).pipe(
          map(variants => ({ ...products[0], variants } as ProductWithVariants)),
        ),
      ),
    );
  }

  // ─── ElasticSearch ─────────────────────────────────────────────────────────

  searchProducts(
    paginator: { pageIndex: number; pageSize: number; },
    sort: { active: string; direction: string; },
    filters: Record<string, any[]>,
    extras?: Record<string, { type?: string; nested?: string; }>,
  ): Promise<ElasticResult<Product>> {
    return this.productDbService.searchProducts(paginator, sort, filters, extras);
  }

  /**
   * Delegates a documents-list ElasticSearch query to the DB service.
   * @param opts - Query options accepted by `ProductDbService.searchDocumentsList`.
   * @returns A Promise resolving to an {@link ElasticResult} containing the matched products.
   */
  searchDocumentsList(opts: Parameters<typeof this.productDbService.searchDocumentsList>[0]): Promise<ElasticResult<Product>> {
    return this.productDbService.searchDocumentsList(opts);
  }

  // ─── Write operations ──────────────────────────────────────────────────────

  // TODO JUAN: este bloque de CRUD hace falta en cliente app? (aparte de si lo usan los creadores)

  createProduct(data: Partial<Product>): Promise<string> {
    return this.productDbService.create<Product>(data);
  }

  /**
   * Creates or overwrites a product document in Firestore.
   * @param data - Partial product data; must include `id` for updates.
   * @returns A Promise resolving to the document ID (new or existing).
   */
  upsertProduct(data: Partial<Product>): Promise<string> {
    return this.productDbService.upsert<Product>(data);
  }

  /**
   * Deletes a product document from Firestore.
   * @param id - The Firestore document ID of the product to delete.
   * @returns A Promise that resolves when deletion is complete.
   */
  deleteProduct(id: string): Promise<void> {
    return this.productDbService.delete(id);
  }

  /**
   * Generates a new unique Firestore document ID without writing to the database.
   * @returns A unique string ID.
   */
  createId(): string {
    return this.productDbService.createId();
  }

  // ─── ElasticSearch ─────────────────────────────────────────────────────────

  // TODO JUAN: esto no debería hacer falta, porque elastic provee de métodos $ para suscribirse on change de refresh
  /**
   * Emits a `Date` whenever the products collection changes, or `null` on error.
   * Useful to trigger cache invalidation of ElasticSearch-based queries.
   * @returns An Observable that emits the timestamp of each change event.
   */
  onProductsChange$(): Observable<Date | null> {
    return this.productDbService.onChange();
  }

  // ─── Related products ──────────────────────────────────────────────────────

  /*
  TODO
  Esta función se puede ELIMINAR porque sus consumidores (shop-page y popup-before-pay) ya no se usan
  */
  /**
   * Fetches active store products ranked by shared category with the given seed products.
   * Out-of-stock products are excluded; `featured` products are prioritised.
   *
   * Used by the legacy callers of {@link ProductListComponent} (`shop` page featured grid and
   * `popup-before-pay`). New callers should prefer {@link getSuggestedProducts$}, which
   * combines occupation-based recommendations with this category-based filler in a single
   * reactive stream.
   *
   * @param seedProducts - Reference products whose categories define the matching criterion.
   *  When empty, all active store products qualify (useful with `onlyFeatured` filtering).
   * @param limit - Maximum number of results to return (defaults to `5`).
   */
  async getRelatedProducts(seedProducts: Array<Product> = [], limit = 5): Promise<Product[]> {
    const products = await this.productDbService.getWhere({
      where: [
        ['type', '==', ProductType.STORE],
        ['status', '==', 'active'],
      ],
      limit: 200,
    }) as Product[];

    const seedIds = new Set(seedProducts.map(p => p.id));
    const refCategoryIds = new Set(
      seedProducts.flatMap(p => (p.categories ?? []).map(c => c.id)),
    );

    return products
      .filter(p => !seedIds.has(p.id) && !isAllProductSoldOut(p))
      .map(p => {
        const categoryScore = refCategoryIds.size
          ? (p.categories ?? []).reduce((acc, c) => acc + (refCategoryIds.has(c.id) ? 1 : 0), 0)
          : 0;
        return { product: p, categoryScore };
      })
      .sort((a, b) => {
        if (a.product.featured !== b.product.featured) return a.product.featured ? -1 : 1;
        return b.categoryScore - a.categoryScore;
      })
      .slice(0, limit)
      .map(entry => entry.product);
  }

  /**
   * Returns the list of products suggested to the user, combining occupation-based
   * recommendations with a category-based fill step to reach a minimum amount.
   *
   * Pipeline:
   *  1. Take the occupation list from {@link getRecommendedProductsForUser$} (falls back to 'default').
   *  2. Remove `excludeProductIds` and sold-out products.
   *  3. If the resulting list has fewer than `min` items, query store products that
   *     share categories with `referenceCategories` and use them to fill up to `min`.
   *  4. Cap the final list to `max` items.
   *
   * If even after filling the list is shorter than `min`, the partial list is returned
   * (no random products are added).
   *
   * @param opts.user$ - The current user stream (used to resolve the occupation list).
   * @param opts.referenceCategories - Categories used as a reference to fill the list (current
   *  product's categories on the product detail page, cart products' categories on the cart).
   * @param opts.excludeProductIds - Product IDs that must never appear (current product, cart items).
   * @param opts.min - Minimum number of products to attempt to return. Defaults to `5`.
   * @param opts.max - Maximum number of products returned. Defaults to `10`.
   */
  getSuggestedProducts$(opts: {
    user$: Observable<UserModel>;
    referenceCategories?: Category[];
    excludeProductIds?: string[];
    min?: number;
    max?: number;
  }): Observable<Product[]> {
    const min = opts.min ?? 5;
    const max = opts.max ?? 10;
    const excludeIds = new Set(opts.excludeProductIds ?? []);
    const refCategoryIds = (opts.referenceCategories ?? []).map(c => c.id);

    return this.getRecommendedProductsForUser$(opts.user$).pipe(
      switchMap(occupationProducts => {
        const filtered = occupationProducts.filter(p =>
          !excludeIds.has(p.id) && !isAllProductSoldOut(p)
        );

        if (filtered.length >= min || !refCategoryIds.length) {
          return of(filtered.slice(0, max));
        }

        const alreadyIncluded = new Set(filtered.map(p => p.id));
        const needed = min - filtered.length;

        return this.getCategoryFillerProducts$(refCategoryIds, excludeIds, alreadyIncluded, needed).pipe(
          map(fillers => [...filtered, ...fillers].slice(0, max))
        );
      })
    );
  }

  /*
  TODO
  Sustituir esa consulta a Firestore con limite 200 por una consulta a Elastic, en la que
  se incluyan los filtros, las restricciones, el orden por `relevance`... todos los filtros que sean necesarios.
  En el caso de esta función, la consulta a elastic debe devolver los ids de los productos, con los que se recuperarán
  de Firestore. La consulta a Elastic devolverá solo el número necesario de ids para completar la lista de sugeridos.
  */
  /**
   * Queries active store products sharing any of the given category ids, scores them
   * by the number of matching categories (and `featured` flag) and returns the top `needed`
   * results after filtering out excluded products and sold-out items.
   *
   * Uses a bounded Firestore query (limit 200) since Firestore cannot filter array-of-objects
   * by a nested field; the category overlap is computed in-memory on the trimmed batch.
   */
  private getCategoryFillerProducts$(
    referenceCategoryIds: string[],
    excludeIds: Set<string>,
    alreadyIncluded: Set<string>,
    needed: number,
  ): Observable<Product[]> {
    return this.productDbService.getWhere$({
      where: [
        ['type', '==', ProductType.STORE],
        ['status', '==', 'active'],
      ],
      limit: 200,
    }).pipe(
      map((products: Product[]) => {
        const refIdSet = new Set(referenceCategoryIds);

        return products
          .filter(p => !excludeIds.has(p.id) && !alreadyIncluded.has(p.id))
          .filter(p => !isAllProductSoldOut(p))
          .map(p => {
            const categoryScore = (p.categories ?? []).reduce(
              (acc, c) => acc + (refIdSet.has(c.id) ? 1 : 0),
              0,
            );
            return { product: p, categoryScore };
          })
          .filter(entry => entry.categoryScore > 0)
          .sort((a, b) => {
            if (a.product.featured !== b.product.featured) return a.product.featured ? -1 : 1;
            return b.categoryScore - a.categoryScore;
          })
          .slice(0, needed)
          .map(entry => entry.product);
      }),
      catchError(() => of([])),
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  //  PRINT ENGINE
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Initialize the print pricing engine from the base print product (id=0).
   * Must be called once at app start after settings are loaded.
   */
  initPrintEngine(printProductDb: ProductPrint): void {
    const product = cloneDeep(printProductDb);

    this.tablePrices = product.variants as Array<PrintVariant>;
    delete product.variants;

    this.weights = product.weights;
    delete product.weights;

    this.blankSheetPercentageCalc = product.extraPrices?.blankSheetPercentageCalc ?? 100;

    this.bindingData = product.bindingData;
    delete product.bindingData;

    this.ringColorPrice = product.ringColorPrice;
    delete product.ringColorPrice;

    this.hardCoverPrice = product.hardCoverPrice;
    delete product.hardCoverPrice;

    this.extraPrices = product.extraPrices;
    if (this.extraPrices?.finishing?.binding) {
      delete this.extraPrices.finishing.binding.weight;
    }
    delete product.extraPrices;
    delete product.ringColors;
    delete product.hardCovers;

    this.printProduct = product;
    this.isInit.next(true);

    this.papers$ = this.getInternalProductWithVariants$('papers');
    this.customizablePapers$ = this.getInternalProductWithVariants$('customizablePapers');

    combineLatest([this.papers$, this.customizablePapers$]).pipe(
      map(([papers, customizablePapers]) => {
        this.combinedPapers = this.mergeVariants(papers, customizablePapers);
      }),
    ).subscribe(); // TODO JUAN: esto cuándo se desuscribe? Si no se retorna la referencia a la suscripción, me parece que never in the lifetime...
  }

  /**
   * Returns the maximum number of sheets per binding for a given paper weight.
   * @param paperWeight - The paper weight to look up.
   * @returns The sheet limit, or `0` if not configured.
   */
  getSheetLimit(paperWeight: PaperWeight): number { // TODO JUAN: getBindingSheetLimit diría yo
    return this.bindingData?.sheetLimit?.[paperWeight] ?? 0; // TODO JUAN: esto es de helper
  }

  // ─── Print product factory ─────────────────────────────────────────────────

  /**
   * Creates a blank {@link ProductPrint} pre-populated with the default print engine settings.
   * @returns A new `ProductPrint` instance ready for customisation.
   */
  newPrintProduct(): ProductPrint { // TODO JUAN: esto es de helper
    return {
      ...cloneDeep(this.printProduct),
      title: '',
      description: '',
      slug: null,
      seo: { title: '', description: '' },
      royalty: 0,
      allowSaleNoStock: true,
      requiresShipping: false,
    } as ProductPrint;
  }

  // ─── Print pricing ─────────────────────────────────────────────────────────

  /**
   * Returns the per-copy base price for the given print settings.
   * @param settings - The active {@link PrintSettings}.
   * @returns The unit price for a single printed copy.
   */
  getPriceOption(settings: PrintSettings): number {
    return getPriceOption(settings, this.tablePrices);
  }

  /**
   * Adjusts a price by removing tax when `mustReduceTax` is enabled.
   * @param price - The gross price to adjust.
   * @returns The adjusted price.
   */
  getAdjustedPrice(price: number): number {
    return getAdjustedPrice(price, this.mustReduceTax);
  }

  /**
   * Computes the total number of printable sides for a document.
   * @param pages - Number of pages in the document.
   * @param settings - The active {@link PrintSettings}.
   * @param coverType - Cover configuration for the file.
   * @returns The total number of sides to be printed.
   */
  getTotalSides(pages: number, settings: PrintSettings, coverType: CoverTypeConfig): number { // TODO JUAN: helper wrapper, no hace falta service
    return getTotalSides(pages, settings, coverType);
  }

  /**
   * Computes the total number of physical sheets required to print a file.
   * @param sides - Total printable sides (as returned by {@link getTotalSides}).
   * @param settings - The active {@link PrintSettings}.
   * @param coverType - Cover configuration for the file.
   * @returns The number of physical sheets needed.
   */
  getFileSheets(sides: number, settings: PrintSettings, coverType: CoverTypeConfig): number { // TODO JUAN: helper wrapper, no hace falta service
    return getFileSheets(sides, settings, coverType);
  }

  /**
   * Calculates the total printing cost for a single file.
   * @param sheets - Number of physical sheets.
   * @param settings - The active {@link PrintSettings}.
   * @param coverType - Cover configuration for the file.
   * @param blankSheets - Number of blank sheets added before/after the file.
   * @param file - The {@link _PrintFile} being priced.
   * @returns The calculated price for printing this file.
   */
  getFilePrice(sheets: number, settings: PrintSettings, coverType: CoverTypeConfig, blankSheets: number, file: _PrintFile): number {
    return getFilePrice(
      sheets, settings, coverType, blankSheets, file,
      this.tablePrices, this.extraPrices, this.blankSheetPercentageCalc,
    );
  }

  /**
   * Calculates the finishing cost (binding, lamination, stapling, etc.) for a file or group.
   * @param fullSheets - Total number of sheets in scope.
   * @param settings - The active {@link PrintSettings}.
   * @param printingGroup - The {@link PrintingGroup} the file belongs to.
   * @param file - The specific {@link _PrintFile}, or `null` when calculating for the whole group.
   * @returns An object with a human-readable `description`, the number of `units`, and the `price`.
   */
  getFinishing(fullSheets: number, settings: PrintSettings, printingGroup: PrintingGroup, file: _PrintFile = null): { description: string; units: number; price: number; } {
    return getFinishing(
      fullSheets, settings, printingGroup, file,
      this.extraPrices, this.ringColorPrice, this.hardCoverPrice,
      this.bindingData, this.printProduct?.printingGroup?.printSettings,
    );
  }

  /**
   * Returns the extra finishing configuration for a given finishing type key.
   * @param type - The finishing type key (e.g. `'binding'`, `'laminated'`).
   * @returns The {@link ExtraFinishingOption} configuration, or `undefined` if not found.
   */
  getFinishingData(type: string): ExtraFinishingOption | undefined { // TODO JUAN: esto es de helper
    return this.extraPrices?.finishing?.[type];
  }

  /**
   * Finds the appropriate binding ring configuration for the given sheet count and paper properties.
   * @param sheetNumber - Number of sheets to bind.
   * @param paperSize - The {@link PageSize} of the document.
   * @param paperWeight - The {@link PaperWeight} of the paper.
   * @param pageTurn - Optional {@link PageTurn} direction.
   * @returns A {@link BindingRingResult} with diameter, quantity, weight, and split information.
   */
  findBindingRing(sheetNumber: number, paperSize: PageSize, paperWeight: PaperWeight, pageTurn?: PageTurn): BindingRingResult {
    return findBindingRing(this.bindingData, sheetNumber, paperSize, paperWeight, pageTurn);
  }

  /**
   * Returns the default value for a given print setting as configured in the base print product.
   * @param setting - The {@link Settings} key or raw string to look up.
   * @returns The default setting value, or `undefined` if not configured.
   */
  getDefaultSettingOption(setting: Settings | string): any {
    return this.printProduct?.printingGroup?.printSettings?.[setting];
  }

  // ─── Printing group weight ─────────────────────────────────────────────────

  /**
   * Calculates the total paper weight (in grams) of all sheets in a printing group.
   * @param printingGroup - The {@link PrintingGroup} to weigh.
   * @returns The total sheets weight in grams.
   */
  getPrintingGroupSheetsWeight(printingGroup: PrintingGroup): number {
    return getPrintingGroupSheetsWeight(printingGroup, this.weights);
  }

  /**
   * Returns the shipping weight for a product snapshot.
   * For print products, uses the stored `printingGroup.weight` or recalculates if absent.
   * @param product - The {@link ProductSnapshot} representing the ordered item.
   * @returns The product weight in grams.
   */
  getProductWeight(product: ProductSnapshot): number {
    if (product.type !== ProductType.PRINT) return product.weight;
    return product.printingGroup?.weight ?? this.getPrintWeight(product);
  }

  /**
   * Calculates the total weight of a print product, including sheets and finishing materials.
   * @param product - The {@link ProductSnapshot} or {@link ProductPrint} to weigh.
   * @returns The total calculated weight in grams.
   */
  getPrintWeight(product: ProductSnapshot | ProductPrint): number {
    let weight = 0;
    if (!product.printingGroup?.printSettings?.paperWeight) {
      if (product.printingGroup?.printSettings) {
        product.printingGroup.printSettings.paperWeight = PaperWeight.W80;
      }
    }
    const wP = this.weights[product.printingGroup?.printSettings?.pageSize]?.[product.printingGroup?.printSettings?.paperWeight] ?? 0;
    weight = (product.printingGroup?.printSheets ?? 0) * wP;

    const finishing = product.printingGroup?.printSettings?.finishing;
    weight += getFinishingWeight(finishing, product, this.extraPrices, false);
    return weight;
  }

  /**
   * Returns the weight contribution of a finishing option (rings, covers, lamination, etc.).
   * @param finishing - The finishing type key.
   * @param product - The product snapshot or print product being evaluated.
   * @param useExtra - When `true`, includes extra weight components (defaults to `true`).
   * @returns The finishing weight in grams.
   */
  getFinishingWeight(finishing: string, product: ProductSnapshot | ProductPrint, useExtra = true): number {
    return getFinishingWeight(finishing, product, this.extraPrices, useExtra);
  }

  // ─── Refresh print group price ─────────────────────────────────────────────

  /**
   * Recomputes the unit price, finishing costs, sheet counts, weight, and binding data for
   * all files in a print product's printing group. Mutates `printProduct` in place.
   * @param printProduct - The {@link ProductPrint} whose pricing data will be updated.
   * @param viewFiles - Optional view-model array mirroring `printProduct.printingGroup.files`;
   *   each entry's `total` is synced after calculation.
   */
  refreshPrintGroupPrice(printProduct: ProductPrint, viewFiles: any[]): void { // TODO JUAN: tipado de viewFiles?
    let subTotal = 0;
    const settings = cloneDeep(printProduct.printingGroup.printSettings);
    let totalGroupSides = 0;
    let fullSheets = 0;
    let totalBlankSheets = 0;
    let _finishing = { description: '', units: 0, price: 0 };
    let ringsWeight = 0;
    let ringsCount = 0;
    let ringsSplit = false;
    let extraWeight = 0;

    let isColor = settings.color === PrintColor.COLOR;
    if (!isColor) {
      isColor = printProduct.printingGroup.files.reduce((acc: boolean, file) => {
        return acc && (file.docColor || (file.coverColor && file.pages === 1));
      }, true);
    }

    const isBinding = [Finishing.BINDING, Finishing.BINDINGX, Finishing.BINDING_LAMINATED].includes(settings.finishing);

    printProduct.printingGroup.files.forEach((file: _PrintFile, i) => {
      if (!file) return;

      const coverType = file.coverType;
      const blankSheets = (file.blankSheetsBefore ?? 0) + (file.blankSheetsAfter ?? 0);
      const totalSides = this.getTotalSides(file.pages, settings, coverType);
      const _fileSheets = this.getFileSheets(totalSides, settings, coverType) + blankSheets;
      file.printSheets = _fileSheets;
      let _fileFinishingPrice = 0;

      if (!settings['grouped'] || settings[Settings.FINISHING] !== Finishing.BINDING) {
        const _fileFinishing = this.getFinishing(_fileSheets, settings, printProduct.printingGroup, file);
        _finishing.description = _fileFinishing.description;
        _finishing.units += _fileFinishing.units;
        _finishing.price += _fileFinishing.price;
        _fileFinishingPrice = _fileFinishing.price;

        if (isBinding) {
          const bindingInfo = findBindingRing(
            this.bindingData, _fileSheets, settings.pageSize, settings.paperWeight, settings.pageTurn,
            this.extraPrices, file, settings,
          );
          file.binding = {
            count: bindingInfo.qty,
            ringSplit: bindingInfo.split,
            ringDiameter: bindingInfo.diameter,
            weight: bindingInfo.weight,
          };
          ringsWeight += bindingInfo.weight;
          ringsCount += bindingInfo.qty;
          ringsSplit = ringsSplit || bindingInfo.split;
        }
      } else {
        if (file.binding) delete file.binding;
      }

      fullSheets += _fileSheets;
      totalGroupSides += totalSides;

      const _settings = cloneDeep(settings);
      _settings.coverColor = file.coverColor;
      _settings.docColor = file.docColor || (file.coverColor && file.pages === 1);
      const total = this.getFilePrice(totalSides, _settings, coverType, blankSheets, file);
      // Persist total on the source file and sync optional view model if present.
      const fileTotal = total + _fileFinishingPrice;
      file.total = fileTotal;
      if (viewFiles?.[i]) {
        viewFiles[i].total = fileTotal;
      }
      if (blankSheets) totalBlankSheets += blankSheets;
      subTotal += total;
    });

    printProduct.printingGroup.printSheets = fullSheets;
    printProduct.printingGroup.printSides = totalGroupSides;
    subTotal = this.getAdjustedPrice(subTotal);
    printProduct.unitPrice.amt = subTotal;

    if (settings['grouped'] && [Finishing.BINDING, Finishing.BINDING_LAMINATED].includes(settings[Settings.FINISHING])) {
      _finishing = this.getFinishing(fullSheets, settings, printProduct.printingGroup, null);
    }

    if (isBinding) {
      if (settings['grouped']) {
        const bindingInfo = findBindingRing(
          this.bindingData, fullSheets, settings.pageSize, settings.paperWeight, settings.pageTurn,
          this.extraPrices, printProduct.printingGroup.files[0], settings,
        );
        printProduct.printingGroup.binding = {
          count: bindingInfo.qty,
          ringSplit: bindingInfo.split,
          ringDiameter: bindingInfo.diameter,
          weight: bindingInfo.weight,
        };
        ringsWeight = bindingInfo.weight;
      } else {
        printProduct.printingGroup.binding = {
          count: ringsCount,
          ringSplit: ringsSplit,
          ringDiameter: null,
          weight: ringsWeight,
        };
      }
    } else {
      printProduct.printingGroup.binding = null;
      extraWeight = this.getFinishingWeight(settings.finishing, printProduct, true);
    }

    const finishing = _finishing;
    finishing.price = this.getAdjustedPrice(finishing.price);
    printProduct.extra = finishing;
    printProduct.copyPrice = this.getPriceOption(settings);

    if (totalBlankSheets) {
      printProduct.extra.blankSheetsUnits = totalBlankSheets;
      printProduct.extra.blankSheetsPrice = this.getAdjustedPrice(
        totalBlankSheets * (this.getPriceOption(settings) * (this.blankSheetPercentageCalc / 100)),
      );
    }

    const sheetsWeight = this.getPrintingGroupSheetsWeight(printProduct.printingGroup);
    printProduct.printingGroup.weight = sheetsWeight + ringsWeight + extraWeight;
  }

  // ─── Custom product pricing ────────────────────────────────────────────────

  /**
   * Calculates the total price for a customisable product based on the selected options.
   * Paper format options with a sheet number are priced per sheet.
   * @param basePrice - The starting base price of the product.
   * @param optionsSelected - Map of selected customisation options (paper size, color, weight, etc.).
   * @param product - Optional product definition used to look up per-sheet prices.
   * @returns A Promise resolving to the total calculated price.
   */
  async getCustomProductPrice(basePrice: number, optionsSelected: any, product?: any): Promise<number> { // TODO JUAN: tipado
    await this.initializeCombinedPapers();
    let price = basePrice;
    const aux = cloneDeep(optionsSelected);

    if (aux?.paperSize && aux.paperColor && aux.paperWeight && aux.paperPattern) {
      aux.paperFormat = {
        paperSize: aux.paperSize,
        paperColor: aux.paperColor,
        paperWeight: aux.paperWeight,
        paperPattern: aux.paperPattern,
      };
      delete aux.paperSize;
      delete aux.paperColor;
      delete aux.paperWeight;
      delete aux.paperPattern;
    }

    Object.entries(aux)?.forEach(([key, value]: [string, any]) => { // TODO JUAN: tipado
      if (!value) return;
      if (key === ProductCustomizableElements.PAPER_FORMAT && aux.sheetNumber) {
        const paperFormat = this.combinedPapers?.find(pap =>
          pap.properties.paperWeight === aux.paperFormat.paperWeight.value &&
          pap.properties.paperColor === aux.paperFormat.paperColor.value &&
          pap.properties.paperPattern === aux.paperFormat.paperPattern.value &&
          pap.properties.paperSize === aux.paperFormat.paperSize.value,
        );
        if (paperFormat && product) {
          const paperFound = product.customElements
            ?.find(item => item.id === 'paperFormat')
            ?.values.find(paper =>
              paper.paperWeight === paperFormat.properties.paperWeight &&
              paper.paperColor === paperFormat.properties.paperColor &&
              paper.paperPattern === paperFormat.properties.paperPattern &&
              paper.paperSize === paperFormat.properties.paperSize,
            );
          if (paperFound) {
            const paperPrice = paperFound.priceBySheetNumber?.find(
              e => e.sheetNumber === aux.sheetNumber.value,
            )?.price ?? paperFound.price ?? 0;
            price += paperPrice * aux.sheetNumber.value;
          }
        }
      }
    });

    return price;
  }

  /**
   * Calculates the total weight for a customisable product based on the selected options.
   * Returns `null` when required options (paper size, color, weight, pattern, sheet number) are absent.
   * @param optionsSelected - Map of selected customisation options.
   * @returns A Promise resolving to the weight in grams, or `null` if options are incomplete.
   */
  async getCustomProductWeight(optionsSelected: any): Promise<number | null> { // TODO JUAN: tipado
    await this.initializeCombinedPapers();
    let weight = 0;
    const aux = cloneDeep(optionsSelected);

    if (!(aux.paperSize && aux.paperColor && aux.paperWeight && aux.sheetNumber && aux.paperPattern)) {
      return null;
    }

    aux.paperFormat = {
      paperWeight: aux.paperWeight,
      paperColor: aux.paperColor,
      paperPattern: aux.paperPattern,
      paperSize: aux.paperSize,
    };
    delete aux.paperSize;
    delete aux.paperColor;
    delete aux.paperPattern;
    delete aux.paperWeight;

    Object.entries(aux).forEach(([key, value]: [string, any]) => { // TODO JUAN: tipado
      if (!value) return;
      if (key === ProductCustomizableElements.PAPER_FORMAT) {
        const paperFormat = this.combinedPapers?.find(pap =>
          pap.properties.paperSize === aux.paperFormat.paperSize.value &&
          pap.properties.paperColor === aux.paperFormat.paperColor.value &&
          pap.properties.paperWeight === aux.paperFormat.paperWeight.value &&
          pap.properties.paperPattern === aux.paperFormat.paperPattern.value,
        );
        if (paperFormat) {
          weight += (this.weights[aux.paperFormat.paperSize.value]?.[aux.paperFormat.paperWeight.value] ?? 0)
            * aux.sheetNumber.value;
        }
      }
      if (key === ProductCustomizableElements.RINGS) {
        weight += findBindingRing(
          this.bindingData,
          aux.sheetNumber.value, aux.paperFormat.paperSize.value,
          aux.paperFormat.paperWeight.value,
        ).weight;
      }
    });

    return weight;
  }

  // ─── Binding uses tracking ─────────────────────────────────────────────────

  /**
   * Populates the `rings` and `covers` arrays on the printing group's binding data,
   * reflecting the actual material usage for the order quantity.
   * No-ops for non-print products or products without binding finishing.
   *
   * In non-grouped binding, each file is expected to carry its own
   * `ringColor`/`hardCoverFront`/`hardCoverBack` (normally propagated by
   * {@link checkIndividualBindingSettings}). When those are missing — which
   * can happen if a file was just pushed and the propagation has not run yet —
   * this method auto-fills them from `printingGroup.printSettings`, so that
   * the binding usage arrays are never accidentally left empty when persisting
   * the order.
   * @param printProduct - Container holding the {@link ProductSnapshot} and the ordered `qty`.
   */
  setPrintProductBindingUses(printProduct: { product: ProductSnapshot; qty: number; }): void {
    const combineObjects = (targetArray: BindingRingUsage[] | BindingCoverUsage[], sourceArray: BindingRingUsage[] | BindingCoverUsage[], keyProps: string[]) => {
      sourceArray.forEach(obj => {
        const match = targetArray.find(t => keyProps.every(k => t[k] === obj[k]));
        if (match) {
          match.uses += obj.uses;
        } else {
          targetArray.push({ ...obj });
        }
      });
    };

    const printingGroup = printProduct.product.printingGroup;
    if (printProduct.product.type !== ProductType.PRINT ||
      printingGroup.printSettings[Settings.FINISHING] !== Finishing.BINDING) {
      return;
    }

    printingGroup.binding.rings = [];
    printingGroup.binding.covers = [];

    if (printingGroup.printSettings.grouped) {
      const diameter = printingGroup.binding.ringDiameter;
      const pageSize = printingGroup.printSettings[Settings.PAGE_SIZE];
      const rings = [{
        color: printingGroup.printSettings[Settings.RING_COLOR].id,
        diameter,
        name: printingGroup.printSettings[Settings.RING_COLOR].name,
        uses: printingGroup.binding.count * printProduct.qty,
      }];

      let covers: BindingCoverUsage[];
      if (printingGroup.printSettings[Settings.HARD_COVER_FRONT].id === printingGroup.printSettings[Settings.HARD_COVER_BACK].id) {
        covers = [{
          color: printingGroup.printSettings[Settings.HARD_COVER_BACK].id,
          name: printingGroup.printSettings[Settings.HARD_COVER_BACK].name,
          pageSize,
          uses: printingGroup.binding.count * 2 * printProduct.qty,
        }];
      } else {
        covers = [{
          color: printingGroup.printSettings[Settings.HARD_COVER_BACK].id,
          name: printingGroup.printSettings[Settings.HARD_COVER_BACK].name,
          pageSize,
          uses: printingGroup.binding.count * printProduct.qty,
        }, {
          color: printingGroup.printSettings[Settings.HARD_COVER_FRONT].id,
          name: printingGroup.printSettings[Settings.HARD_COVER_FRONT].name,
          pageSize,
          uses: printingGroup.binding.count * printProduct.qty,
        }];
      }
      combineObjects(printingGroup.binding.rings, rings, ['color', 'diameter']);
      combineObjects(printingGroup.binding.covers, covers, ['color', 'pageSize']);
    } else {
      printingGroup.files.forEach((file, index) => {
        // Defensive auto-fill: if a file is missing any of the per-file binding
        // settings (e.g. it was just pushed before checkIndividualBindingSettings
        // ran), populate them from the group-level printSettings. Without this
        // the file would be silently skipped and the resulting binding.rings /
        // binding.covers arrays would be left empty, persisting a broken order.
        if (!file[Settings.RING_COLOR]
          || !file[Settings.HARD_COVER_FRONT]
          || !file[Settings.HARD_COVER_BACK]) {
          this.addBindingDataToFile(printProduct, index, printingGroup.printSettings);
        }
        const diameter = file?.binding?.ringDiameter;
        const pageSize = printingGroup.printSettings[Settings.PAGE_SIZE];

        const rings = [{
          color: file[Settings.RING_COLOR].id,
          diameter,
          name: file[Settings.RING_COLOR].name,
          uses: file.binding.count * printProduct.qty,
        }];

        let covers: BindingCoverUsage[];
        if (file[Settings.HARD_COVER_FRONT]?.id === file[Settings.HARD_COVER_BACK]?.id) {
          covers = [{
            color: file[Settings.HARD_COVER_BACK].id,
            name: file[Settings.HARD_COVER_BACK].name,
            pageSize,
            uses: file.binding.count * 2 * printProduct.qty,
          }];
        } else {
          covers = [{
            color: file[Settings.HARD_COVER_BACK].id,
            name: file[Settings.HARD_COVER_BACK].name,
            pageSize,
            uses: file.binding.count * printProduct.qty,
          }, {
            color: file[Settings.HARD_COVER_FRONT].id,
            name: file[Settings.HARD_COVER_FRONT].name,
            pageSize,
            uses: file.binding.count * printProduct.qty,
          }];
        }
        file.binding.covers = covers;
        file.binding.rings = rings;
        combineObjects(printingGroup.binding.rings, file.binding.rings, ['color', 'diameter']);
        combineObjects(printingGroup.binding.covers, file.binding.covers, ['color', 'pageSize']);
      });
    }
  }

  // ─── Doc colors / binding settings ─────────────────────────────────────────

  /**
   * Recomputes `docColor` and `coverColor` flags on the print settings based on
   * the individual file's color choices. Mutates `printSettings` in place.
   * @param printProduct - Container holding the {@link ProductSnapshot} to inspect.
   */
  checkDocColorsExceptions(printProduct: { product: ProductSnapshot; }): void {
    const printSettings = printProduct.product.printingGroup.printSettings;
    const files = printProduct.product.printingGroup.files;
    const colorCountChecked = files.filter(f => f.docColor).length;
    printSettings.docColor = printSettings.color === 'color'
      ? colorCountChecked === files.length
      : !!colorCountChecked;
    const coverColorCountChecked = files.filter(f => f.coverColor).length;
    printSettings.coverColor = !!coverColorCountChecked;
  }

  /**
   * Ensures each file in a non-grouped binding group has its own ring and cover settings.
   * On the first pass (no event) it initialises missing binding data; on subsequent calls
   * it propagates the changed setting across all files.
   * @param printProduct - Container holding the {@link ProductSnapshot} to update.
   * @param event - The setting-change event, or `null` for an initial population pass.
   * @param settings - The full {@link PrintSettingsConfig} providing option lists.
   */
  checkIndividualBindingSettings(printProduct: { product: ProductSnapshot; }, event: { setting: string; option: number; } | null, settings: PrintSettingsConfig): void {
    const _printSettings = cloneDeep(printProduct.product.printingGroup.printSettings);
    if (![Finishing.BINDING, Finishing.BINDING_LAMINATED].includes(_printSettings.finishing)) return;

    printProduct.product.printingGroup.files.forEach((file, index) => {
      if (!file.ringColor) {
        this.addBindingDataToFile(printProduct, index, _printSettings);
      } else if (event && ['ringColor', 'hardCoverFront', 'hardCoverBack'].includes(event.setting) && !_printSettings.grouped) {
        _printSettings[event.setting] = settings[event.setting].options[event.option - 1].value;
        this.addBindingDataToFile(printProduct, index, _printSettings, event.setting);
      }
    });
  }

  /**
   * Resets the paper weight for all file cover types and the printing group settings,
   * then refreshes pricing. Optionally updates the UI settings model and emits a change event.
   * @param printProduct - Container holding the {@link ProductSnapshot} to update.
   * @param paperWeight - The new {@link PaperWeight} to apply.
   * @param homeSettings - Optional UI settings model to update the selected paper weight index.
   * @param optionEmitter - Optional EventEmitter to broadcast the setting change.
   */
  restartPaperWeight(printProduct: { product: ProductSnapshot; }, paperWeight: PaperWeight, homeSettings?: any, optionEmitter?: any): void { // TODO JUAN: homeSettings es uno de los settings de print products? Podría definirse aparte y extenderse
    const files = printProduct.product.printingGroup.files;
    files.forEach(file => {
      file.coverType.paperWeight = paperWeight;
    });
    printProduct.product.printingGroup.printSettings.paperWeight = paperWeight;

    if (homeSettings && optionEmitter) {
      const optionIndex = homeSettings.paperWeight.options.findIndex(o => o.value === paperWeight) + 1;
      const prev = homeSettings[Settings.PAPER_WEIGHT].selected;
      homeSettings[Settings.PAPER_WEIGHT].selected = optionIndex;
      optionEmitter.emit({
        setting: Settings.PAPER_WEIGHT,
        option: optionIndex,
        prev,
      });
    }
    this.refreshPrintGroupPrice(printProduct.product as ProductPrint, files);
  }

  /**
   * Returns a user-facing warning message for the current printing group configuration,
   * covering stapling orientation and binding sheet-limit overflows.
   * @param printingGroup - The {@link PrintingGroup} to inspect.
   * @returns A localised warning string, or an empty string if no warning applies.
   */
  getWarningMessage(printingGroup: PrintingGroup): string {
    const paperWeight = printingGroup.printSettings.paperWeight;
    const sheetLimit = this.bindingData?.sheetLimit?.[paperWeight];
    const laminationWeight = printingGroup.files[0]?.laminationWeight;
    const sheetLimitLamination = this.bindingData?.sheetLimitLamination?.[laminationWeight];
    const grouped = printingGroup.printSettings.grouped;
    const files = printingGroup.files;

    if (printingGroup.printSettings.finishing === Finishing.STAPLED) {
      return 'Si tus documentos contienen páginas en horizontal y vertical, estas no se rotarán de forma automática.';
    }

    if ([Finishing.BINDING, Finishing.BINDING_LAMINATED].includes(printingGroup.printSettings.finishing)) {
      const numberOfBindings = printingGroup.binding?.count ?? 0;
      const showBindingMessage = grouped ? numberOfBindings > 1 : numberOfBindings > files.length;
      if (showBindingMessage) {
        if (printingGroup.printSettings.finishing === Finishing.BINDING) {
          return `Para el grosor de ${paperWeight} gr el máximo es de ${sheetLimit} folios por cada encuadernación. Superados ${sheetLimit} folios se crearán tantas encuadernaciones como sean necesarias.`;
        }
        return `El máximo es de ${sheetLimitLamination} folios por cada encuadernación. Superados ${sheetLimitLamination} folios se crearán tantas encuadernaciones como sean necesarias.`;
      }
    }

    return '';
  }

  // ─── Private helpers ───────────────────────────────────────────────────────

  /**
   * Copies binding-related settings (ring color, hard covers) from `printSettings` onto a file.
   * When `setting` is provided only that specific property is updated; otherwise all three are set.
   * @param printProduct - Container holding the {@link ProductSnapshot} whose file will be updated.
   * @param index - Zero-based index of the file within `printingGroup.files`.
   * @param printSettings - Source {@link PrintSettings} to read values from.
   * @param setting - Specific setting key to update, or `null` to update all binding fields.
   */
  private addBindingDataToFile(printProduct: { product: ProductSnapshot; }, index: number, printSettings: PrintSettings, setting: string = null): void {
    if (setting) {
      printProduct.product.printingGroup.files[index][setting] = printSettings[setting];
    } else {
      printProduct.product.printingGroup.files[index].ringColor = printSettings.ringColor;
      printProduct.product.printingGroup.files[index].hardCoverFront = printSettings.hardCoverFront;
      printProduct.product.printingGroup.files[index].hardCoverBack = printSettings.hardCoverBack;
    }
  } // TODO JUAN: helper

  /**
   * Concatenates the variant arrays of two paper products into a single flat list.
   * @param papers - The standard papers product.
   * @param customizablePapers - The customisable papers product.
   * @returns A merged array of {@link ProductVariant} objects.
   */
  private mergeVariants(papers: ProductWithVariants, customizablePapers: ProductWithVariants): ProductVariant[] { // TODO JUAN: helper
    return [...(papers?.variants as ProductVariant[] ?? []), ...(customizablePapers?.variants as ProductVariant[] ?? [])];
  }

  // ─── Compound cart-item setter ─────────────────────────────────────────────

  /**
   * Refreshes the print product price and updates item.price.amt.
   * Accepts a loosely-typed cart item so the domain stays free of ShoppingCart dep.
   */
  setPrintProduct(item: { product: ProductSnapshot; price: { amt: number; }; qty: number; }, files: _PrintFile[]): void {
    this.refreshPrintGroupPrice(item.product as ProductPrint, files);
    item.price.amt = item.qty * (item.product.unitPrice.amt + (item.product.extra?.price ?? 0));
    this.setPrintProductBindingUses(item);
  }

  // ─── Print labels (UI) ─────────────────────────────────────────────────────

  /**
   * Generates a human-readable array of setting labels for display in the UI (legacy format).
   * @param settings - A flat map of print setting keys to their current values.
   * @returns An ordered array of localised label strings (one per active setting).
   */
  getPrintLabels(settings: Record<string, any>): string[] { // TODO JUAN: tipado + helper
    const labels: string[] = [];
    const keys = [
      Settings.PAGE_SIZE, Settings.PAPER_WEIGHT, Settings.COLOR,
      Settings.TWO_SIDED, Settings.PAGES_PER_SHEET, Settings.AUTO_ROTATE,
      Settings.PAGE_ORIENTATION, Settings.PAGE_TURN, Settings.FINISHING,
    ];
    const labelSetting: any = {
      [Settings.PAGE_SIZE]: { [PageSize.A3]: 'A3', [PageSize.A4]: 'A4', [PageSize.A5]: 'A5' },
      [Settings.PAPER_WEIGHT]: { [PaperWeight.W80]: '80 g/m2', [PaperWeight.W100]: '100 g/m2', [PaperWeight.W300]: '300 g/m2' },
      [Settings.PAGE_ORIENTATION]: { [PageOrientation.LANDSCAPE]: 'Folio: Horizontal', [PageOrientation.PORTRAIT]: 'Folio: Vertical' },
      [Settings.COLOR]: { [PrintColor.BLACKNWHITE]: 'B/N', [PrintColor.COLOR]: 'Color' },
      [Settings.PAGES_PER_SHEET]: { [PagesPerSheet.ONE]: '1 Pág. x Cara', [PagesPerSheet.TWO]: '2 Pág. x Cara', [PagesPerSheet.FOUR]: '4 Pág. x Cara' },
      [Settings.TWO_SIDED]: { true: 'Doble Cara', false: 'Una Cara' },
      [Settings.PAGE_TURN]: { [PageTurn.SHORTSIDE]: 'Pasar Pág: Borde Corto', [PageTurn.LARGESIDE]: 'Pasar Pág: Borde Largo' },
      [Settings.FINISHING]: {
        [Finishing.BINDING]: 'Encuadernación', [Finishing.BINDINGX]: 'Encuadernación lado corto',
        [Finishing.STAPLED]: 'Grapado', [Finishing.LAMINATED]: 'Plastificado',
        [Finishing.PERFORATED4]: 'Taladrado 4 agujeros', [Finishing.PERFORATED2]: 'Taladrado 2 agujeros',
        [Finishing.NONE]: 'Sin Acabado',
      },
      [Settings.GROUPED]: { true: 'Agrupados', false: 'Individuales' },
      [Settings.COVER_COLOR]: { false: '', true: 'Portada a Color' },
      [Settings.COVER_LAMINATED]: { false: '', true: 'Portada plastificada a color' },
      [Settings.DOC_COLOR]: { false: '', true: 'Doc. a Color' },
      [Settings.AUTO_ROTATE]: { false: 'Rotación Automática: No', true: 'Rotación Automática: Sí' },
    };
    keys.forEach(e => {
      if (settings[e] === undefined) return;
      let label = labelSetting[e][settings[e]];
      if (e === Settings.COLOR && settings[Settings.COLOR] === PrintColor.BLACKNWHITE) {
        if (settings[Settings.COVER_COLOR] && settings[Settings.DOC_COLOR] && !settings[Settings.COVER_LAMINATED]) label += ` (Mixto)`;
        else if (settings[Settings.COVER_COLOR] && settings[Settings.DOC_COLOR] && settings[Settings.COVER_LAMINATED]) label += ` (Mixto y portada plastificada)`;
        else if (settings[Settings.COVER_COLOR]) label += settings[Settings.COVER_LAMINATED] ? ` (${labelSetting[Settings.COVER_LAMINATED][settings[Settings.COVER_LAMINATED]]})` : ` (${labelSetting[Settings.COVER_COLOR][settings[Settings.COVER_COLOR]]})`;
        else if (settings[Settings.DOC_COLOR]) label += settings[Settings.COVER_LAMINATED] ? ` (${labelSetting[Settings.DOC_COLOR][settings[Settings.DOC_COLOR]]} y ${labelSetting[Settings.COVER_LAMINATED][settings[Settings.COVER_LAMINATED]]})` : ` (${labelSetting[Settings.DOC_COLOR][settings[Settings.DOC_COLOR]]})`;
      }
      if (e === Settings.COLOR && settings[Settings.COLOR] === PrintColor.COLOR) {
        if (settings[Settings.COVER_LAMINATED]) label += ` (${labelSetting[Settings.COVER_LAMINATED][settings[Settings.COVER_LAMINATED]]})`;
      }
      if (e === Settings.FINISHING && (settings[Settings.FINISHING] === Finishing.BINDING || settings[Settings.FINISHING] === Finishing.BINDINGX)) {
        const grouped = settings[Settings.GROUPED] === true || settings[Settings.GROUPED] === undefined;
        const _labels = [labelSetting[Settings.GROUPED][grouped.toString()]];
        if (settings[Settings.RING_COLOR]) _labels.push(`${settings[Settings.RING_COLOR]?.name}`);
        if (settings[Settings.HARD_COVER_FRONT] && !settings[Settings.COVER_LAMINATED]) _labels.push(`${settings[Settings.HARD_COVER_FRONT].name}`);
        if (settings[Settings.HARD_COVER_BACK]) _labels.push(`${settings[Settings.HARD_COVER_BACK].name}`);
        label += ` (${_labels.join(', ')})`;
      }
      labels.push(label);
    });
    return labels;
  }

  /**
   * Generates a structured array of setting label objects for the new-format UI badges.
   * Each object contains at minimum a `label` and optionally `description`, `type`, and `image`.
   * @param settings - A flat map of print setting keys to their current values.
   * @param files - The list of {@link _PrintFile} objects used to detect mixed color modes.
   * @returns An ordered array of label descriptor objects.
   */
  getNewPrintLabels(settings: Record<string, any>, files: _PrintFile[]): any[] { // TODO JUAN: tipado + helper
    const labels: any[] = [];
    const keys = [
      Settings.COLOR, Settings.PAGE_SIZE, Settings.PAPER_WEIGHT, Settings.TWO_SIDED,
      Settings.PAGE_ORIENTATION, Settings.PAGE_TURN, Settings.PAGES_PER_SHEET, Settings.FINISHING,
    ];
    const labelSetting: any = {
      [Settings.PAGE_SIZE]: {
        [PageSize.A3]: { label: 'A3', description: 'Formato A3' },
        [PageSize.A4]: { label: 'A4', description: 'Formato A4' },
        [PageSize.A5]: { label: 'A5', description: 'Formato A5' },
      },
      [Settings.PAPER_WEIGHT]: {
        [PaperWeight.W80]: { label: '80gr', description: '80 gramos' },
        [PaperWeight.W90]: { label: '90gr', description: '90 gramos' },
        [PaperWeight.W100]: { label: '100gr', description: '100 gramos' },
        [PaperWeight.W120]: { label: '120gr', description: '120 gramos' },
        [PaperWeight.W160]: { label: '160gr', description: '160 gramos' },
        [PaperWeight.W200]: { label: '200gr', description: '200 gramos' },
        [PaperWeight.W300]: { label: '300gr', description: '300 gramos' },
      },
      [Settings.COLOR]: {
        [PrintColor.BLACKNWHITE]: { label: 'B/N', description: 'Blanco y negro', type: 'image', image: 'assets/icon/black-white-indicator.svg' },
        [PrintColor.COLOR]: { label: 'Color', description: 'Color', type: 'image', image: 'assets/icon/color-indicator.svg' },
      },
      [Settings.PAGES_PER_SHEET]: {
        [PagesPerSheet.ONE]: { label: '1PagCara', description: '1 página por cara' },
        [PagesPerSheet.TWO]: { label: '2PagCara', description: '2 página por cara' },
        [PagesPerSheet.FOUR]: { label: '4PagCara', description: '4 página por cara' },
      },
      [Settings.TWO_SIDED]: {
        true: { label: '2Cara', description: 'A doble cara' },
        false: { label: '1Cara', description: 'A una cara' },
      },
      [Settings.PAGE_ORIENTATION]: {
        [PageOrientation.PORTRAIT]: { label: 'Vert', description: 'Vertical' },
        [PageOrientation.LANDSCAPE]: { label: 'Hori', description: 'Horizontal' },
      },
      [Settings.PAGE_TURN]: {
        [PageTurn.LARGESIDE]: { label: 'B. Largo', description: 'Paso de página por el borde largo' },
        [PageTurn.SHORTSIDE]: { label: 'B. Corto', description: 'Paso de página por el borde corto' },
      },
      [Settings.FINISHING]: {
        [Finishing.BINDING]: { label: 'Encuadernación', type: 'finishing' },
        [Finishing.BINDING_LAMINATED]: { label: 'Encuadernación plastificada', type: 'finishing' },
        [Finishing.BINDINGX]: { label: 'Encuadernación lado corto', type: 'finishing' },
        [Finishing.STAPLED]: { label: 'Grapado', type: 'finishing' },
        [Finishing.LAMINATED]: { label: 'Plastificado', type: 'finishing' },
        [Finishing.PERFORATED4]: { label: 'Taladrado 4 agujeros', type: 'finishing' },
        [Finishing.PERFORATED2]: { label: 'Taladrado 2 agujeros', type: 'finishing' },
        [Finishing.NONE]: { label: 'Sin Acabado', type: 'finishing' },
      },
    };
    for (const e of keys) {
      if (settings[e] === undefined) break;
      let label = labelSetting[e][settings[e]];
      if (e === Settings.COLOR) {
        if (files.some(file => !file.docColor)) { labels.push(labelSetting[e][PrintColor.BLACKNWHITE]); }
        if (files.some(file => file.docColor) || files.some(file => file.coverColor)) { labels.push(labelSetting[e][PrintColor.COLOR]); }
        continue;
      }
      if (e === Settings.FINISHING && [Finishing.BINDING, Finishing.BINDING_LAMINATED].includes(settings[Settings.FINISHING])) {
        label = { ...labelSetting[e][settings[Settings.FINISHING]] };
        label.label += settings[Settings.GROUPED] ? ' agrupada' : ' individual';
        labels.push(label);
        continue;
      }
      labels.push(label);
    }
    return labels;
  }

  /**
   * Lazily initialises `combinedPapers` by waiting for the first emission of both
   * `papers$` and `customizablePapers$` streams, then merging their variants.
   * Subsequent calls are no-ops if `combinedPapers` is already populated.
   * @returns A Promise that resolves once `combinedPapers` is ready.
   */
  private async initializeCombinedPapers(): Promise<void> {
    if (this.combinedPapers) return;
    this.combinedPapers = await firstValueFrom(
      combineLatest([this.papers$, this.customizablePapers$]).pipe(
        map(([papers, customizablePapers]) => this.mergeVariants(papers, customizablePapers)),
      ),
    );
  }

  // ─── Print settings management ────────────────────────────────────────────

  /**
   * Signals all subscribers of `settingsChanged$` that the print settings have been updated.
   * Emits the current timestamp as the change marker.
   */
  refreshPrintSettings(): void {
    this._settingsChanged.next(new Date());
  }

  /**
   * Applies a new value for a print setting on the given PrintSettings object
   * and recomputes autoRotate accordingly.
   */
  updatePrintGroupSettings(printSettings: PrintSettings, setting: string, value: any): void {
    printSettings[setting] = value;
    const autoRotate = printSettings[Settings.PAGES_PER_SHEET] === PagesPerSheet.ONE
      && printSettings[Settings.FINISHING] !== Finishing.STAPLED;
    printSettings['autoRotate'] = autoRotate;  // TODO JUAN: 'autoRotate' debería estar definido como setting para no cablear aquí el string
    this.refreshPrintSettings();
  }

  // ─── Recommended products ─────────────────────────────────────────────────

  /**
   * Returns an ordered list of recommended products for the given user,
   * falling back to the 'default' occupation list when needed.
   */
  getRecommendedProductsForUser$(user$: Observable<UserModel>, excludeProductId?: string): Observable<Product[]> {
    return user$.pipe(
      switchMap(user => {
        const occupation = user?.ocupation;
        if (occupation && occupation !== 'none' && occupation !== 'default') {
          return this.getProductsForOccupation$(occupation).pipe(
            switchMap(products => products?.length ? of(products) : this.getProductsForOccupation$('default'))
          );
        }
        return this.getProductsForOccupation$('default');
      }),
      map(products => excludeProductId ? products.filter(p => p.id !== excludeProductId) : products)
    );
  }

  /**
   * Fetches the recommended product IDs for an occupation from settings, then resolves them to products.
   * @param occupation - The occupation key to look up.
   * @returns An Observable emitting an array of active {@link Product} objects.
   */
  private getProductsForOccupation$(occupation: string): Observable<Product[]> {
    return this.settingsService.getRecommendedProductIdsByOccupation$(occupation).pipe(
      switchMap(ids => this.getProductsByIds$(ids))
    );
  }

  /**
   * Fetches multiple products by their IDs in parallel and returns only active ones,
   * preserving the original order of `productIds`.
   * @param productIds - Array of Firestore document IDs to fetch.
   * @returns An Observable emitting an array of active {@link Product} objects in the given order.
   */
  getProductsByIds$(productIds: string[]): Observable<Product[]> {
    if (!productIds?.length) return of([]);
    return forkJoin(
      productIds.map(id => this.productDbService.get$(id).pipe(take(1)))
    ).pipe(
      map(products => productIds
        .map((_, i) => products[i])
        .filter((p): p is Product => p?.status === 'active')
      ),
      catchError(() => of([]))
    );
  }
}
